'use client';

import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import { useEffect, useRef, useState } from 'react';

import { Card, CardContent } from '@/components/ui/card';

const MESSAGES = [
  "Waking up your salon's database…",
  'Dusting off the appointment book…',
  'Restarting the till…',
  'Brewing coffee for the servers…',
  'Unlocking the front door…',
  'Warming up the chairs…',
  'Polishing the mirrors…',
  'Stocking the shelves…',
  'Firing up the card reader…',
  'Fluffing the towels…',
  'Testing the hair dryers…',
  'Counting the register…',
  'Almost ready…',
];

const MESSAGE_INTERVAL_MS = 1700;
const TOTAL_DURATION_MS = MESSAGES.length * MESSAGE_INTERVAL_MS;

export function TenantWakingOverlay({
  forceShow = false,
}: {
  forceShow?: boolean;
}) {
  const [messageIndex, setMessageIndex] = useState(0);
  const [progressStarted, setProgressStarted] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    intervalRef.current = setInterval(() => {
      setMessageIndex((i) => {
        const next = i + 1;
        if (next >= MESSAGES.length) {
          if (intervalRef.current) clearInterval(intervalRef.current);
          return i;
        }
        return next;
      });
    }, MESSAGE_INTERVAL_MS);
    // Trigger after a paint so the 0% -> 100% transition actually animates.
    const timeout = setTimeout(() => setProgressStarted(true), 50);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      clearTimeout(timeout);
    };
  }, []);

  return (
    <div className='fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm'>
      <Card className='mx-4 w-full max-w-xl'>
        <CardContent className='flex flex-col items-center gap-4 p-8 text-center'>
          <div className='aspect-video w-full'>
            <DotLottieReact
              src='/simple.json'
              loop
              autoplay
              className='h-full w-full'
            />
          </div>
          <p className='min-h-10 text-sm text-muted-foreground'>
            {MESSAGES[messageIndex]}
          </p>
          <div className='h-1.5 w-full overflow-hidden rounded-full bg-muted'>
            <div
              className='h-full rounded-full bg-primary'
              style={{
                width: progressStarted ? '100%' : '0%',
                transition: `width ${TOTAL_DURATION_MS}ms ease-out`,
              }}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
